import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useEndReasonTypeData } from '../contexts';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

// Map of EndReasonTypeId to display names
const END_REASON_TYPE_OPTIONS = [
  { id: 1, name: 'Drop Out' },
  { id: 2, name: 'Withdrawal' },
  { id: 3, name: 'N/A' },
  { id: 4, name: 'Other' },
  { id: 5, name: 'Graduated' },
  { id: 6, name: 'Persisted Next Grade' },
  { id: 7, name: 'Persisted Same Grade' },
  { id: 8, name: 'Graduated With Rigorous' },
  { id: 9, name: 'Graduated Without Rigorous' },
  { id: 10, name: 'Deceased' },
  { id: 11, name: 'Unknown' },
];

export const EndReasonTypeFilterControls = () => {
  const { filter, setFilter, activeSchoolYear, data } = useEndReasonTypeData();
  const [availableYears, setAvailableYears] = useState<number[]>([]);

  // Get unique end reason types that exist in the data
  const availableTypes = React.useMemo(() => {
    const typeIds = new Set(data.map(d => d.endReasonTypeId));
    return END_REASON_TYPE_OPTIONS.filter(opt => typeIds.has(opt.id));
  }, [data]);

  useEffect(() => {
    if (activeSchoolYear) {
      const years = Array.from({ length: 10 }, (_, i) => activeSchoolYear - i);
      setAvailableYears(years);
    }
  }, [activeSchoolYear]);

  const handleYearChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value;
    setFilter({
      ...filter,
      schoolYears: typeof value === 'string' ? [parseInt(value)] : value,
    });
  };

  const handleTypeChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value;
    setFilter({
      ...filter,
      selectedEndReasonTypes: typeof value === 'string' ? [parseInt(value)] : value,
    });
  };

  const handleClearYears = () => {
    if (activeSchoolYear) {
      setFilter({ ...filter, schoolYears: [activeSchoolYear] });
    }
  };

  const handleClearTypes = () => {
    setFilter({ ...filter, selectedEndReasonTypes: [] });
  };

  return (
    <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel>School Year</InputLabel>
          <Select
            multiple
            value={filter.schoolYears}
            onChange={handleYearChange}
            input={<OutlinedInput label="School Year" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((year) => (
                  <Chip key={year} label={`${year}-${year + 1}`} size="small" />
                ))}
              </Box>
            )}
            MenuProps={MenuProps}
          >
            {availableYears.map((year) => (
              <MenuItem key={year} value={year}>
                {`${year}-${year + 1}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {filter.schoolYears.length > 1 && (
          <Tooltip title="Reset to current year">
            <IconButton size="small" onClick={handleClearYears}>
              <ClearIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel>End Reason Type</InputLabel>
          <Select
            multiple
            value={filter.selectedEndReasonTypes || []}
            onChange={handleTypeChange}
            input={<OutlinedInput label="End Reason Type" />}
            renderValue={(selected) => {
              if (selected.length === 0) return 'All Types';
              return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((typeId) => {
                    const type = END_REASON_TYPE_OPTIONS.find(t => t.id === typeId);
                    return (
                      <Chip key={typeId} label={type?.name || typeId} size="small" />
                    );
                  })}
                </Box>
              );
            }}
            MenuProps={MenuProps}
          >
            {availableTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {filter.selectedEndReasonTypes && filter.selectedEndReasonTypes.length > 0 && (
          <Tooltip title="Show all types">
            <IconButton size="small" onClick={handleClearTypes}>
              <ClearIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Stack>
  );
};